import { getDateWithoutTime } from '@/app/components/search/utils/dates/getDateWithoutTime.ts';

export enum LOCALE_EXCEPTION {
  BGBG = 'bg-BG',
  CSCZ = 'cs-CZ',
  ISIS = 'is-IS',
  LTLT = 'lt-LT',
  PTPT = 'pt-PT',
  SKSK = 'sk-SK',
}

export const SPECIFIC_SHORT_MONTHS = {
  [LOCALE_EXCEPTION.BGBG]: ['яну', 'фев', 'мар', 'апр', 'май', 'юни', 'юли', 'авг', 'сеп', 'окт', 'ное', 'дек'],
  [LOCALE_EXCEPTION.ISIS]: ['jan', 'feb', 'mar', 'apr', 'maí', 'jún', 'júl', 'ágú', 'sep', 'okt', 'nóv', 'des'],
  [LOCALE_EXCEPTION.LTLT]: [
    'saus.',
    'vas.',
    'kov.',
    'bal.',
    'geg.',
    'birž.',
    'liep.',
    'rugp.',
    'rugs.',
    'spal.',
    'lapkr.',
    'gruod.',
  ],
  [LOCALE_EXCEPTION.CSCZ]: [],
  [LOCALE_EXCEPTION.PTPT]: [],
  [LOCALE_EXCEPTION.SKSK]: [],
};

export const isExceptionLocale = (locale: string): boolean =>
  (Object.values(LOCALE_EXCEPTION) as string[]).includes(locale);

export const getShortMonth = (locale: string, date: string): string => {
  if (SPECIFIC_SHORT_MONTHS[locale as LOCALE_EXCEPTION].length > 0) {
    const monthNum = new Date(date).getMonth();
    return SPECIFIC_SHORT_MONTHS[locale as LOCALE_EXCEPTION][monthNum];
  }
  return new Date(date).toLocaleString(locale, { month: 'short' });
};

export const DATE_SHORT_REPLACE = {
  [LOCALE_EXCEPTION.SKSK]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/\d+\.$/, `${shortMonth}.`),
  [LOCALE_EXCEPTION.CSCZ]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/\d+\.$/, `${shortMonth}.`),
  [LOCALE_EXCEPTION.PTPT]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/\/\d+$/, ` ${shortMonth}`),
  [LOCALE_EXCEPTION.LTLT]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/,(.*)/, `, ${shortMonth}`),
  [LOCALE_EXCEPTION.BGBG]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/.*,/, `${shortMonth},`),
  [LOCALE_EXCEPTION.ISIS]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/\d+\.$/, `${shortMonth}.`),
};

export const DATE_SHORT_DAY_MONTH_REPLACE = {
  [LOCALE_EXCEPTION.SKSK]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/\d+\.$/, `${shortMonth}.`),
  [LOCALE_EXCEPTION.CSCZ]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/\d+\.$/, `${shortMonth}.`),
  [LOCALE_EXCEPTION.PTPT]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/\/\d+$/, ` ${shortMonth}`),
  [LOCALE_EXCEPTION.LTLT]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/.*-/, `${shortMonth}-`),
  [LOCALE_EXCEPTION.BGBG]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/\..*/, `.${shortMonth}`),
  [LOCALE_EXCEPTION.ISIS]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/\d+.$/, `${shortMonth}.`),
};

export const DATE_SHORT_MONTH_YEAR_REPLACE = {
  [LOCALE_EXCEPTION.SKSK]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/^(.*?)\//, `${shortMonth}. `),
  [LOCALE_EXCEPTION.CSCZ]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/^(.*?)\//, `${shortMonth}. `),
  [LOCALE_EXCEPTION.PTPT]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/^(.*?)\//, `${shortMonth} `),
  [LOCALE_EXCEPTION.LTLT]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/-.*/, `-${shortMonth}`),
  [LOCALE_EXCEPTION.BGBG]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/^[^.]*/, `${shortMonth}`),
  [LOCALE_EXCEPTION.ISIS]: (dateNumericMonth: string, shortMonth: string): string =>
    dateNumericMonth.replace(/^[^.]*/, `${shortMonth}`),
};

export const formatDateShortException = (locale: string, date: string): string => {
  const dateNumericMonth = getDateWithoutTime(date).toLocaleString(locale, {
    day: 'numeric',
    weekday: 'short',
    month: 'numeric',
  });
  const shortMonth = getShortMonth(locale, date);
  return DATE_SHORT_REPLACE[locale as LOCALE_EXCEPTION](dateNumericMonth, shortMonth);
};

export const formatDateShortDayMonthException = (locale: string, date: string): string => {
  const dateNumericMonth = getDateWithoutTime(date).toLocaleString(locale, {
    day: 'numeric',
    month: 'numeric',
  });
  const shortMonth = getShortMonth(locale, date);
  return DATE_SHORT_DAY_MONTH_REPLACE[locale as LOCALE_EXCEPTION](dateNumericMonth, shortMonth);
};

export const formatDateShortMonthYearException = (locale: string, date: string): string => {
  const dateNumericMonth = getDateWithoutTime(date).toLocaleString(locale, {
    month: 'numeric',
    year: 'numeric',
  });
  const shortMonth = getShortMonth(locale, date);
  return DATE_SHORT_MONTH_YEAR_REPLACE[locale as LOCALE_EXCEPTION](dateNumericMonth, shortMonth);
};
